import { useEffect, useState, useMemo, useRef } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Search, List, Database, GlobeLock, Radar } from 'lucide-react'
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '@/components/ui/form'
import { layersApi } from '@/features/layers/api'
import { locationsApi } from '@/features/locations/api'
import { objectsApi } from '../api'
import { formatSecondsToDHM } from '../utils/formatTime'
import { IconPicker } from './IconPicker'
import type { ObjectFormData } from '../schema'
import { AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface ObjectDetailsTabProps {
    form: UseFormReturn<ObjectFormData>
    isEditing: boolean
    isService?: boolean
}

export function ObjectDetailsTab({ form, isEditing, isService = false }: ObjectDetailsTabProps) {
    const { t } = useTranslation()
    const [objectIDError, setObjectIDError] = useState<string | null>(null)
    const [isCheckingObjectID, setIsCheckingObjectID] = useState(false)
    const [technologySearch, setTechnologySearch] = useState('')
    const [teamSearch, setTeamSearch] = useState('')
    const [backupSolutionSearch, setBackupSolutionSearch] = useState('')
    const [datastoreConfirmDialog, setDatastoreConfirmDialog] = useState<{
        open: boolean
        type: 'set' | 'clear' | null
        pendingTechnology: number | null
    }>({
        open: false,
        type: null,
        pendingTechnology: null,
    })
    const objectID = form.watch('ObjectID')
    const managementOnly = form.watch('ManagementOnly')
    const technology = form.watch('Technology')
    const datastore = form.watch('Datastore')

    // Fetch dropdown data
    const { data: layersData } = useQuery({
        queryKey: ['layers'],
        queryFn: async () => {
            const response = await layersApi.list({ page: 1, limit: 1000 })
            return response.data.data || []
        },
    })

    const { data: locationsData } = useQuery({
        queryKey: ['locations'],
        queryFn: async () => {
            const response = await locationsApi.list({ page: 1, limit: 1000 })
            return response.data.data || []
        },
    })

    const { data: teamsData } = useQuery({
        queryKey: ['teams'],
        queryFn: async () => {
            const response = await objectsApi.getTeams()
            return response.data?.data || response.data || []
        },
    })

    const { data: technologiesData, error: technologiesError, isLoading: technologiesLoading } = useQuery({
        queryKey: ['technologies'],
        queryFn: async () => {
            try {
                const response = await objectsApi.getTechnologies()
                console.log('Technologies API response:', response)
                const data = response.data?.data || response.data || []
                console.log('Technologies data:', data)
                return data
            } catch (error) {
                console.error('Error fetching technologies:', error)
                return []
            }
        },
    })

    const { data: backupSolutionsData, isLoading: backupSolutionsLoading, error: backupSolutionsError } = useQuery({
        queryKey: ['backupSolutions'],
        queryFn: async () => {
            const response = await objectsApi.getBackupSolutions()
            console.log('Backup solutions API response:', response)
            const data = response.data?.data || response.data || []
            console.log('Backup solutions data:', data)
            return data
        },
    })

    // Check ObjectID uniqueness
    useEffect(() => {
        if (!objectID || objectID.trim() === '' || isEditing) {
            setObjectIDError(null)
            return
        }

        const checkTimeout = setTimeout(async () => {
            setIsCheckingObjectID(true)
            try {
                const response = await objectsApi.checkObjectIDUnique(objectID)
                const isUnique = response.data?.data?.isUnique ?? response.data?.isUnique ?? false
                if (!isUnique) {
                    setObjectIDError(t('objects.objectIDNotUnique'))
                    form.setError('ObjectID', { message: t('objects.objectIDNotUnique') })
                } else {
                    setObjectIDError(null)
                    form.clearErrors('ObjectID')
                }
            } catch (error) {
                console.error('Error checking ObjectID uniqueness:', error)
            } finally {
                setIsCheckingObjectID(false)
            }
        }, 500) // Debounce

        return () => clearTimeout(checkTimeout)
    }, [objectID, isEditing, form, t])

    // Track previous technology to detect changes
    const prevTechnologyRef = useRef<number | null>(null)
    
    // Handle Technology category change - if Database and Datastore is not 1, show confirmation dialog
    useEffect(() => {
        // Only show dialog if technology actually changed (not on initial load)
        if (technology && technologiesData && prevTechnologyRef.current !== technology) {
            const selectedTech = technologiesData.find((t: any) => t.id === technology)
            if (selectedTech?.category === 'Database' && datastore !== 1) {
                // Show confirmation dialog to set datastore
                setDatastoreConfirmDialog({
                    open: true,
                    type: 'set',
                    pendingTechnology: technology,
                })
            } else if (selectedTech?.category !== 'Database' && datastore === 1) {
                // Show confirmation dialog to clear datastore
                setDatastoreConfirmDialog({
                    open: true,
                    type: 'clear',
                    pendingTechnology: technology,
                })
            }
            prevTechnologyRef.current = technology
        } else if (!technology) {
            prevTechnologyRef.current = null
        }
    }, [technology, technologiesData, datastore])
    
    // Handle datastore confirmation dialog actions
    const handleDatastoreConfirm = () => {
        if (datastoreConfirmDialog.type === 'set') {
            form.setValue('Datastore', 1)
        } else if (datastoreConfirmDialog.type === 'clear') {
            form.setValue('Datastore', 0)
        }
        setDatastoreConfirmDialog({ open: false, type: null, pendingTechnology: null })
    }
    
    const handleDatastoreCancel = () => {
        // Revert technology selection if user cancels
        if (datastoreConfirmDialog.pendingTechnology !== null) {
            form.setValue('Technology', prevTechnologyRef.current)
        }
        setDatastoreConfirmDialog({ open: false, type: null, pendingTechnology: null })
    }

    // Handle ManagementOnly change - if false, set BackupSolution to 0
    useEffect(() => {
        if (managementOnly === 0) {
            form.setValue('BackupSolution', 0)
        }
    }, [managementOnly, form])

    // RPO/RTO options (common values in minutes)
    const rpoRtoOptions = [
        { value: '0', label: '0 minutes' },
        { value: '15', label: '15 minutes' },
        { value: '30', label: '30 minutes' },
        { value: '60', label: '1 hour' },
        { value: '120', label: '2 hours' },
        { value: '240', label: '4 hours' },
        { value: '480', label: '8 hours' },
        { value: '1440', label: '24 hours' },
    ]

    const formatMinutes = (minutes: number | string | null): string => {
        if (!minutes) return ''
        const mins = typeof minutes === 'string' ? parseInt(minutes) : minutes
        return formatSecondsToDHM(mins * 60)
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Column 1 - Labels with fields underneath */}
                <div className="space-y-4">
                    <FormField
                        control={form.control}
                        name="ObjectID"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>
                                    {t('objects.objectID')} <span className="text-destructive">*</span>
                                </FormLabel>
                                {isEditing && (
                                    <FormDescription className="text-destructive text-xs">
                                        {t('objects.objectIDCannotChange')}
                                    </FormDescription>
                                )}
                                <FormControl>
                                    <Input
                                        {...field}
                                        placeholder={t('objects.objectIDPlaceholder')}
                                        disabled={isEditing}
                                        className="text-left max-w-md"
                                    />
                                </FormControl>
                                {!isEditing && objectID && objectID.trim() !== '' ? (
                                    <div className="h-5 flex items-center gap-1.5 text-xs mt-1">
                                        {isCheckingObjectID ? (
                                            <span className="text-muted-foreground">{t('objects.checkingObjectID', 'Checking...')}</span>
                                        ) : objectIDError ? (
                                            <span className="text-destructive font-medium">{objectIDError}</span>
                                        ) : (
                                            <span className="text-green-600">{t('objects.objectIDAvailable', 'Available')}</span>
                                        )}
                                    </div>
                                ) : (
                                    <div className="h-5"><FormMessage /></div>
                                )}
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="Description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('objects.description')}</FormLabel>
                                <FormControl>
                                    <Textarea
                                        {...field}
                                        value={field.value || ''}
                                        placeholder={t('objects.descriptionPlaceholder')}
                                        rows={4}
                                        className="resize-none text-left max-w-md"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="LayerID"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>
                                    {t('objects.layerID')} <span className="text-destructive">*</span>
                                </FormLabel>
                                {isEditing && (
                                    <FormDescription className="text-destructive text-xs">
                                        {t('objects.layerIDCannotChange')}
                                    </FormDescription>
                                )}
                                <Select
                                    value={field.value?.toString() || ''}
                                    onValueChange={(value) => {
                                        field.onChange(value ? parseInt(value) : null)
                                    }}
                                    disabled={isEditing}
                                >
                                    <FormControl>
                                        <SelectTrigger className="text-left max-w-md">
                                            <SelectValue placeholder={t('objects.selectLayer')} />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {layersData?.map((layer: any) => (
                                            <SelectItem key={layer.id} value={layer.layerID?.toString() || ''}>
                                                {layer.layerID ? `${layer.layerID} - ${layer.description || ''}` : layer.description || ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="ResponsibleTeam"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('objects.responsibleTeam')}</FormLabel>
                                <Select
                                    value={field.value?.toString() || ''}
                                    onValueChange={(value) => {
                                        field.onChange(value ? parseInt(value) : null)
                                    }}
                                >
                                    <FormControl>
                                        <SelectTrigger className="text-left max-w-md">
                                            <SelectValue placeholder={t('objects.selectTeam')} />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        <div className="px-2 py-1.5 border-b">
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                <Input
                                                    placeholder={t('objects.searchTeams')}
                                                    value={teamSearch}
                                                    onChange={(e) => setTeamSearch(e.target.value)}
                                                    className="pl-8 h-8"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                        {teamsData
                                            ?.filter((team: any) => {
                                                if (!teamSearch) return true
                                                const searchLower = teamSearch.toLowerCase()
                                                const teamName = (team.teamName || '').toLowerCase()
                                                return teamName.includes(searchLower)
                                            })
                                            .map((team: any) => (
                                                <SelectItem key={team.id} value={team.id.toString()}>
                                                    {team.teamName}
                                                </SelectItem>
                                            ))}
                                        {teamsData && teamsData.filter((team: any) => {
                                            if (!teamSearch) return true
                                            const searchLower = teamSearch.toLowerCase()
                                            const teamName = (team.teamName || '').toLowerCase()
                                            return teamName.includes(searchLower)
                                        }).length === 0 && teamSearch && (
                                            <SelectItem value="no-results" disabled>
                                                {t('objects.noTeamsFound')}
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="Location"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('objects.location')}</FormLabel>
                                <Select
                                    value={field.value?.toString() || ''}
                                    onValueChange={(value) => {
                                        field.onChange(value ? parseInt(value) : null)
                                    }}
                                >
                                    <FormControl>
                                        <SelectTrigger className="text-left max-w-md">
                                            <SelectValue placeholder={t('objects.selectLocation')} />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {locationsData?.map((location: any) => (
                                            <SelectItem key={location.id} value={location.id.toString()}>
                                                {location.description}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {!isService && (
                        <FormField
                            control={form.control}
                            name="Technology"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('objects.technology')}</FormLabel>
                                    <Select
                                        value={field.value?.toString() || ''}
                                        onValueChange={(value) => {
                                            prevTechnologyRef.current = field.value || null
                                            if (value === 'clear') {
                                                field.onChange(null)
                                            } else {
                                                field.onChange(value ? parseInt(value) : null)
                                            }
                                        }}
                                    >
                                        <FormControl>
                                            <SelectTrigger className="text-left max-w-md">
                                                <SelectValue placeholder={t('objects.selectTechnology')} />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <div className="px-2 py-1.5 border-b">
                                                <div className="relative">
                                                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                    <Input
                                                        placeholder={t('objects.searchTechnologies')}
                                                        value={technologySearch}
                                                        onChange={(e) => setTechnologySearch(e.target.value)}
                                                        className="pl-8 h-8"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onKeyDown={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                            </div>
                                            {technologiesLoading ? (
                                                <SelectItem value="loading" disabled>
                                                    Loading...
                                                </SelectItem>
                                            ) : technologiesError ? (
                                                <SelectItem value="error" disabled>
                                                    Error loading technologies
                                                </SelectItem>
                                            ) : technologiesData && technologiesData.length > 0 ? (
                                                <>
                                                    <SelectItem value="clear" className="text-muted-foreground">
                                                        {t('common.clear')}
                                                    </SelectItem>
                                                    {technologiesData
                                                        .filter((tech: any) => {
                                                            if (!technologySearch) return true
                                                            const searchLower = technologySearch.toLowerCase()
                                                            const name = (tech.name || '').toLowerCase()
                                                            const manufacturer = (tech.manufacturer || '').toLowerCase()
                                                            const category = (tech.category || '').toLowerCase()
                                                            const model = (tech.model || '').toLowerCase()
                                                            return name.includes(searchLower) ||
                                                                manufacturer.includes(searchLower) ||
                                                                category.includes(searchLower) ||
                                                                model.includes(searchLower)
                                                        })
                                                        .map((tech: any) => (
                                                            <SelectItem key={tech.id} value={tech.id.toString()}>
                                                                {tech.name}
                                                            </SelectItem>
                                                        ))}
                                                </>
                                            ) : (
                                                <SelectItem value="empty" disabled>
                                                    No technologies available
                                                </SelectItem>
                                            )}
                                            {technologiesData && technologiesData.filter((tech: any) => {
                                                if (!technologySearch) return true
                                                const searchLower = technologySearch.toLowerCase()
                                                const name = (tech.name || '').toLowerCase()
                                                const manufacturer = (tech.manufacturer || '').toLowerCase()
                                                const category = (tech.category || '').toLowerCase()
                                                const model = (tech.model || '').toLowerCase()
                                                return name.includes(searchLower) ||
                                                    manufacturer.includes(searchLower) ||
                                                    category.includes(searchLower) ||
                                                    model.includes(searchLower)
                                            }).length === 0 && technologiesData.length > 0 && technologySearch && (
                                                <SelectItem value="no-results" disabled>
                                                    {t('objects.noTechnologiesFound')}
                                                </SelectItem>
                                            )}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    )}
                </div>

                {/* Column 2 - Icon, Checkboxes, RPO, RTO */}
                <div className="space-y-4">
                    <FormField
                        control={form.control}
                        name="Icon"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('objects.icon')}</FormLabel>
                                <FormControl>
                                    <IconPicker
                                        value={field.value || null}
                                        onChange={field.onChange}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    {!isService && (
                        <FormField
                            control={form.control}
                            name="ManagementOnly"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value === 1}
                                            onCheckedChange={(checked) => {
                                                field.onChange(checked ? 1 : 0)
                                            }}
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel className="flex items-center gap-2">
                                            <List className="h-4 w-4 text-muted-foreground" />
                                            {t('objects.managementOnly')}
                                        </FormLabel>
                                    </div>
                                </FormItem>
                            )}
                        />
                    )}

                    {!isService && managementOnly === 1 && (
                        <FormField
                            control={form.control}
                            name="BackupSolution"
                            render={({ field }) => {
                                return (
                                    <FormItem>
                                        <FormLabel>{t('objects.backupSolution')}</FormLabel>
                                        <Select
                                            value={field.value?.toString() || ''}
                                            onValueChange={(value) => {
                                                if (value === 'clear') {
                                                    field.onChange(null)
                                                } else {
                                                    field.onChange(value ? parseInt(value) : null)
                                                }
                                            }}
                                            disabled={backupSolutionsLoading}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={backupSolutionsLoading ? t('common.loading') : t('objects.selectBackupSolution')} />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <div className="px-2 py-1.5 border-b">
                                                    <div className="relative">
                                                        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                        <Input
                                                            placeholder={t('objects.searchBackupSolutions')}
                                                            value={backupSolutionSearch}
                                                            onChange={(e) => setBackupSolutionSearch(e.target.value)}
                                                            className="pl-8 h-8"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                </div>
                                                {backupSolutionsError && (
                                                    <SelectItem value="error" disabled>
                                                        {t('common.error')}: {backupSolutionsError.message}
                                                    </SelectItem>
                                                )}
                                                {backupSolutionsData && backupSolutionsData.length > 0 ? (
                                                    <>
                                                        <SelectItem value="clear" className="text-muted-foreground">
                                                            {t('common.clear')}
                                                        </SelectItem>
                                                        {backupSolutionsData
                                                            .filter((solution: any) => {
                                                                if (!backupSolutionSearch) return true
                                                                const searchLower = backupSolutionSearch.toLowerCase()
                                                                const name = (solution.name || '').toLowerCase()
                                                                return name.includes(searchLower)
                                                            })
                                                            .map((solution: any) => (
                                                                <SelectItem key={solution.id} value={solution.id.toString()}>
                                                                    {solution.name}
                                                                </SelectItem>
                                                            ))}
                                                    </>
                                                ) : (
                                                    !backupSolutionsLoading && (
                                                        <SelectItem value="no-data" disabled>
                                                            {t('objects.noBackupSolutions')}
                                                        </SelectItem>
                                                    )
                                                )}
                                                {backupSolutionsData && backupSolutionsData.filter((solution: any) => {
                                                    if (!backupSolutionSearch) return true
                                                    const searchLower = backupSolutionSearch.toLowerCase()
                                                    const name = (solution.name || '').toLowerCase()
                                                    return name.includes(searchLower)
                                                }).length === 0 && backupSolutionsData.length > 0 && backupSolutionSearch && (
                                                    <SelectItem value="no-results" disabled>
                                                        {t('objects.noBackupSolutionsFound')}
                                                    </SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )
                            }}
                        />
                    )}

                    {!isService && (
                        <FormField
                            control={form.control}
                            name="Datastore"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value === 1}
                                            onCheckedChange={(checked) => {
                                                field.onChange(checked ? 1 : 0)
                                            }}
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel className="flex items-center gap-2">
                                            <Database className="h-4 w-4 text-muted-foreground" />
                                            {t('objects.datastore')}
                                        </FormLabel>
                                    </div>
                                </FormItem>
                            )}
                        />
                    )}

                    {!isService && (
                        <FormField
                            control={form.control}
                            name="ApplicationEncryption"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value === 1}
                                            onCheckedChange={(checked) => {
                                                field.onChange(checked ? 1 : 0)
                                            }}
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel className="flex items-center gap-2">
                                            <GlobeLock className="h-4 w-4 text-muted-foreground" />
                                            {t('objects.applicationEncryption')}
                                        </FormLabel>
                                    </div>
                                </FormItem>
                            )}
                        />
                    )}

                    {!isService && (
                        <FormField
                            control={form.control}
                            name="Analytics"
                            render={({ field }) => (
                                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value === 'Yes'}
                                            onCheckedChange={(checked) => {
                                                field.onChange(checked ? 'Yes' : 'No')
                                            }}
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel className="flex items-center gap-2">
                                            <Radar className="h-4 w-4 text-muted-foreground" />
                                            {t('objects.analytics')}
                                        </FormLabel>
                                    </div>
                                </FormItem>
                            )}
                        />
                    )}

                    {!isService && (
                        <FormField
                            control={form.control}
                            name="RPO"
                            render={({ field }) => {
                                const currentValue = field.value?.toString() || ''
                                const isDropdownValue = rpoRtoOptions.some(opt => opt.value === currentValue)
                                const customMinutes = isDropdownValue ? '' : (typeof field.value === 'number' ? field.value : (currentValue ? parseInt(currentValue) : ''))
                                
                                return (
                                    <FormItem>
                                        <FormLabel>{t('objects.rpo')}</FormLabel>
                                        <div className="max-w-md space-y-2">
                                            <Select
                                                value={isDropdownValue ? currentValue : 'custom'}
                                                onValueChange={(value) => {
                                                    if (value === 'custom') {
                                                        // Keep current custom value if it exists
                                                        if (customMinutes) {
                                                            field.onChange(customMinutes)
                                                        } else {
                                                            field.onChange(null)
                                                        }
                                                    } else {
                                                        field.onChange(value)
                                                    }
                                                }}
                                            >
                                                <FormControl>
                                                    <SelectTrigger className="text-left">
                                                        <SelectValue placeholder={t('objects.selectRPO')} />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {rpoRtoOptions.map((option) => (
                                                        <SelectItem key={option.value} value={option.value}>
                                                            {option.label} ({formatMinutes(option.value)})
                                                        </SelectItem>
                                                    ))}
                                                    <SelectItem value="custom">{t('objects.customMinutes')}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <Input
                                                type="number"
                                                placeholder={t('objects.enterMinutes')}
                                                value={customMinutes}
                                                onChange={(e) => {
                                                    const value = e.target.value
                                                    if (value) {
                                                        field.onChange(parseInt(value))
                                                    } else {
                                                        field.onChange(null)
                                                    }
                                                }}
                                                className="text-left"
                                            />
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )
                            }}
                        />
                    )}

                    <FormField
                        control={form.control}
                        name="RTO"
                        render={({ field }) => {
                            const currentValue = field.value?.toString() || ''
                            const isDropdownValue = rpoRtoOptions.some(opt => opt.value === currentValue)
                            const customMinutes = isDropdownValue ? '' : (typeof field.value === 'number' ? field.value : (currentValue ? parseInt(currentValue) : ''))
                            
                            return (
                                <FormItem>
                                    <FormLabel>{t('objects.rto')}</FormLabel>
                                    <div className="max-w-md space-y-2">
                                        <Select
                                            value={isDropdownValue ? currentValue : 'custom'}
                                            onValueChange={(value) => {
                                                if (value === 'custom') {
                                                    // Keep current custom value if it exists
                                                    if (customMinutes) {
                                                        field.onChange(customMinutes)
                                                    } else {
                                                        field.onChange(null)
                                                    }
                                                } else {
                                                    field.onChange(value)
                                                }
                                            }}
                                        >
                                            <FormControl>
                                                <SelectTrigger className="text-left">
                                                    <SelectValue placeholder={t('objects.selectRTO')} />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {rpoRtoOptions.map((option) => (
                                                    <SelectItem key={option.value} value={option.value}>
                                                        {option.label} ({formatMinutes(option.value)})
                                                    </SelectItem>
                                                ))}
                                                <SelectItem value="custom">{t('objects.customMinutes')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Input
                                            type="number"
                                            placeholder={t('objects.enterMinutes')}
                                            value={customMinutes}
                                            onChange={(e) => {
                                                const value = e.target.value
                                                if (value) {
                                                    field.onChange(parseInt(value))
                                                } else {
                                                    field.onChange(null)
                                                }
                                            }}
                                            className="text-left"
                                        />
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )
                        }}
                    />
                </div>
            </div>
            
            {/* Datastore Confirmation Dialog */}
            <Dialog open={datastoreConfirmDialog.open} onOpenChange={(open) => {
                if (!open) {
                    handleDatastoreCancel()
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {datastoreConfirmDialog.type === 'set' 
                                ? t('objects.confirmSetDatastore')
                                : t('objects.confirmClearDatastore')}
                        </DialogTitle>
                        <DialogDescription>
                            {datastoreConfirmDialog.type === 'set' ? (
                                <>
                                    {t('objects.confirmSetDatastoreMessage')}
                                    <br /><br />
                                    <strong>{t('objects.datastoreAlignmentInfo')}</strong>
                                </>
                            ) : (
                                t('objects.confirmClearDatastoreMessage')
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={handleDatastoreCancel}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={handleDatastoreConfirm}
                        >
                            {datastoreConfirmDialog.type === 'set' 
                                ? t('objects.setDatastore')
                                : t('objects.clearDatastore')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
